// Admin: list of quotation requests (with optional lead); status column and filters; link to detail

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuotationRequests } from '@/hooks/use-store';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import type { QuotationRequest, QuotationRequestStatus } from '@/lib/store-types';
import { normalizeQuotationRequestStatus } from '@/lib/store-types';
import type { Lead } from '@/lib/store-types';
import { Eye, Plus, Search } from 'lucide-react';

type QuotationRequestWithLead = QuotationRequest & { lead?: Lead };

/** Etiquetas de estado (alineadas con AdminQuotationRequestDetail). */
const STATUS_LABELS: Record<QuotationRequestStatus, string> = {
  draft: 'Borrador',
  pending: 'Enviado',
  en_revision: 'En revisión',
  cotizado: 'Cotizado',
  finished: 'Finalizado',
  pending_deposit_payment: 'Pendiente pago depósito',
  in_process: 'En proceso',
  shipped: 'En camino y Pend. pago balance',
  pending_final_payment: 'En camino y Pend. pago balance',
  arriving_soon: 'En camino',
  delivered: 'Entregado',
};

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { dateStyle: 'short' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Número de cotización (últimos 8 caracteres del id en mayúsculas). */
function quotationNumber(id: string): string {
  return id.slice(-8).toUpperCase();
}

export default function AdminQuotationRequests() {
  const { data: requests, isLoading } = useQuotationRequests();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [leadFilter, setLeadFilter] = useState<string>('all'); // 'all' | 'with_lead' | 'without_lead'
  const [requestNumberSearch, setRequestNumberSearch] = useState('');

  const filtered = useMemo(() => {
    const list = requests ?? [];
    let out = list;
    if (statusFilter !== 'all') {
      out = out.filter((r) => normalizeQuotationRequestStatus(r.status) === statusFilter);
    }
    if (leadFilter === 'with_lead') {
      out = out.filter((r) => r.lead_id != null);
    } else if (leadFilter === 'without_lead') {
      out = out.filter((r) => r.lead_id == null);
    }
    if (requestNumberSearch.trim()) {
      const q = requestNumberSearch.trim().toUpperCase().replace(/-/g, '');
      out = out.filter((r) => {
        const idNorm = r.id.replace(/-/g, '').toUpperCase();
        const last8 = idNorm.slice(-8);
        return idNorm.includes(q) || last8.includes(q);
      });
    }
    return out;
  }, [requests, statusFilter, leadFilter, requestNumberSearch]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Solicitudes de cotización</h1>
          <p className="text-zinc-600 mt-1">Filtra por estado, número de solicitud o con/sin lead. Pulse Ver para ver detalle.</p>
        </div>
        <Button asChild className="gap-2 shrink-0">
          <Link to="/store/admin/quotation-requests/new">
            <Plus className="h-4 w-4" />
            Nueva solicitud
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            type="text"
            placeholder="Nº solicitud (ej. 3D98806A)"
            className="pl-8 w-[200px]"
            value={requestNumberSearch}
            onChange={(e) => setRequestNumberSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {(Object.keys(STATUS_LABELS) as QuotationRequestStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={leadFilter} onValueChange={setLeadFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Lead" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="with_lead">Con lead</SelectItem>
            <SelectItem value="without_lead">Sin lead</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-zinc-500">Cargando...</p>
      ) : (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="font-semibold">Fecha</TableHead>
                <TableHead className="font-semibold">Nº cotización</TableHead>
                <TableHead className="font-semibold">Estado</TableHead>
                <TableHead className="font-semibold">Lead</TableHead>
                <TableHead className="font-semibold">Ítems</TableHead>
                <TableHead className="font-semibold max-w-[200px]">Especificaciones</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((qr: QuotationRequestWithLead) => (
                <TableRow key={qr.id} className="hover:bg-zinc-50/50">
                  <TableCell className="text-zinc-700 whitespace-nowrap">{formatDate(qr.created_at)}</TableCell>
                  <TableCell className="font-mono text-zinc-900">{quotationNumber(qr.id)}</TableCell>
                  <TableCell className="text-zinc-700">
                    {STATUS_LABELS[normalizeQuotationRequestStatus(qr.status)]}
                  </TableCell>
                  <TableCell className="text-zinc-700">
                    {qr.lead ? (
                      <Link to={`/store/admin/leads/${qr.lead.id}`} className="text-primary hover:underline">
                        {qr.lead.name ?? qr.lead.email}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-700">{Array.isArray(qr.items) ? qr.items.length : 0}</TableCell>
                  <TableCell className="text-zinc-500 text-sm max-w-[200px] truncate" title={qr.specifications ?? ''}>
                    {qr.specifications ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" asChild className="gap-1">
                      <Link to={`/store/admin/quotation-requests/${qr.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                        Ver
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
